/**
 * © 2025 Grupo Salamander SAPI de CV
 *
 * List component overrides for the Renaissance design system.
 * Covers MuiListItem, MuiListItemButton, MuiListItemIcon, MuiListItemText,
 * MuiMenuItem. Row geometry (radius, min height) and feedback motion come
 * from the redesign tokens (plan task 1.1).
 */

import { colorConverter } from '../colorConverter'
import type { TPaletteDefinition } from '../palette'
import { focusRing } from '../focusRing'
import {
  density,
  radii,
  radius,
  spacing,
  sizing,
  transitionFor,
  type TDensity,
} from '../tokens'

/**
 * List icons sit a little larger than the row title. The title size is a rem
 * string since the redesign typography port, so the offset is added in CSS.
 */
function iconFontSize(theme: {
  typography: { title?: { fontSize?: unknown } }
}): number | string {
  const size = theme.typography.title?.fontSize
  if (typeof size === 'number') return size + sizing.iconTitleOffset
  if (typeof size === 'string')
    return `calc(${size} + ${sizing.iconTitleOffset}px)`
  return 16
}

export function listOverrides(
  palette: TPaletteDefinition,
  mode: 'light' | 'dark' = 'light',
  densityKey: TDensity = 'regular',
  reducedMotion = false
) {
  const d = density[densityKey]
  const selectedBg = colorConverter(palette.primary.main, 0.15)
  const selectedHoverBg = colorConverter(palette.primary.main, 0.2)
  const ring = focusRing(palette, mode)

  return {
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: radii.sm,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: radius.row,
          minHeight: d.rowHeight,
          paddingTop: spacing.lg,
          paddingBottom: spacing.lg,
          transition: transitionFor(
            'feedback',
            ['background-color'],
            reducedMotion
          ),
          '&.Mui-selected': {
            backgroundColor: selectedBg,
            '&:hover': {
              backgroundColor: selectedHoverBg,
            },
          },
          '&.Mui-disabled': {
            opacity: 0.5,
          },
          '&.Mui-focusVisible': ring,
        },
      },
      variants: [
        {
          props: { variant: 'sidebar' as const },
          style: {
            borderRadius: radii.lg,
            paddingTop: spacing.sm,
            paddingBottom: spacing.sm,
            paddingLeft: spacing.xl,
            paddingRight: spacing.sm,
            marginBottom: spacing.xs,
            marginTop: spacing.xs,
            '&.Mui-selected': {
              backgroundColor: selectedBg,
              '&:hover': {
                backgroundColor: selectedHoverBg,
              },
            },
            '&.Mui-disabled': {
              opacity: 0.5,
            },
            '&.Mui-focusVisible': ring,
          },
        },
      ],
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          color: 'inherit',
          // MUI 9 changed the default from 56px to theme.spacing(4.5) (36px);
          // keep the v7 width so list-row geometry stays unchanged.
          minWidth: 56,
          fontSize: iconFontSize(theme),
        }),
      },
      variants: [
        {
          props: { variant: 'sidebar' as const },
          style: ({ theme }: { theme: any }) => ({
            minWidth: 'unset',
            color: 'inherit',
            marginRight: spacing.md,
            fontSize: iconFontSize(theme),
          }),
        },
      ],
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          margin: spacing.none,
        },
        primary: ({ theme }: { theme: any }) => ({
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontWeight: theme.typography.title?.fontWeight || 400,
          fontSize: theme.typography.title?.fontSize || 16,
          color: palette.text.main,
        }),
        secondary: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: sizing.listSecondaryFont,
          color: palette.textSecondary.main,
        },
      },
      variants: [
        {
          props: { variant: 'sidebar' as const },
          style: ({ theme }: { theme: any }) => ({
            '& .MuiListItemText-primary': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontWeight: 400,
              fontSize: theme.typography.title?.fontSize,
              color: palette.text.main,
            },
            '& .MuiListItemText-secondary': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: sizing.listSecondaryFont,
              color: palette.textSecondary.main,
            },
          }),
        },
      ],
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          minHeight: d.rowHeight,
          borderRadius: radius.row,
          '&.Mui-focusVisible': ring,
        },
      },
    },
  }
}
